/**
 * @fileoverview Revenue-by-country and ad-spend-by-platform breakdown cards,
 * rendered as donut charts matching the dashboard's donut visual language
 * (AdPlatformSpendDonut) but fed product-scoped data via props.
 *
 * UX rules:
 * - Slices below 5% share are merged into a muted "Others" slice so leader
 *   labels can never cluster into unreadable overlaps (a lone small slice is
 *   kept — it has no neighbor to collide with).
 * - Single-entry lists render a centered "single source" state at donut
 *   height instead of a one-slice ring, keeping the two cards symmetric.
 * Replaces the old Revenue-vs-Costs chart, whose totals already live in the
 * hero cards.
 * @module features/home/components/product-statistics/ProductBreakdowns
 */

import type { ReactElement } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Cell, Label, Pie, PieChart } from 'recharts'
import { BsMeta } from 'react-icons/bs'
import { cn } from '@/shared/lib/utils'
import { ChartContainer, ChartTooltip } from '@/components/ui/chart'
import type { ChartConfig } from '@/components/ui/chart'
import { RechartsHoverAdapter } from '@/shared/components/ui/RechartsHoverAdapter'
import { DonutSliceGradients } from '@/shared/components/ui/DonutSliceGradients'
import { donutSliceGradientFill } from '@/shared/utils/donutGradient'
import { getDonutChartColors } from '@/components/ui/chart-utils'
import { useCompactMoney } from '@/shared/hooks/useCompactMoney'
import { codeToFlag } from '@/shared/utils/countryUtils'
import { GoogleAdsIcon } from '@/shared/components/ui/icons/GoogleAdsIcon'
import type { CountryMetrics } from '@/features/home/hooks/useProductStatistics'
import { aggregateSlices, type BreakdownSlice } from '@/features/home/utils/breakdownSlices'

interface ProductBreakdownsProps {
  /** Revenue per country, sorted descending (zero-sales entries pre-filtered). */
  countries: CountryMetrics[]
  /** Ad spend per platform key (e.g. META, GOOGLE). */
  platformBreakdown: Record<string, number>
  className?: string
}

function platformIcon(platform: string, className: string): ReactElement | null {
  const normalized = platform.toLowerCase()
  if (normalized.includes('meta') || normalized.includes('facebook')) {
    return <BsMeta className={className} aria-hidden="true" />
  }
  if (normalized.includes('google')) {
    return <GoogleAdsIcon className={className} />
  }
  return null
}

/**
 * Icon variant for SVG chart labels: sized via width/height ATTRIBUTES, since
 * CSS classes are not reliably applied to svg-in-svg nesting.
 */
function platformLabelIcon(platform: string): ReactElement | null {
  const normalized = platform.toLowerCase()
  if (normalized.includes('meta') || normalized.includes('facebook')) {
    return <BsMeta size={21} aria-hidden="true" />
  }
  if (normalized.includes('google')) {
    return <GoogleAdsIcon size={21} />
  }
  return null
}

export function ProductBreakdowns({
  countries,
  platformBreakdown,
  className,
}: ProductBreakdownsProps): ReactElement | null {
  const { t } = useTranslation('features-home')
  const colors = getDonutChartColors()

  // Dedicated categorical hues for country slices — must not reuse semantic
  // legend tokens (google blue, profit emerald, ...) or slices read as those.
  const countryPalette = [
    'var(--chart-cat-1)',
    'var(--chart-cat-2)',
    'var(--chart-cat-3)',
    'var(--chart-cat-4)',
    'var(--chart-cat-5)',
  ]
  const othersStyle = { name: t('productStatistics.breakdowns.others'), fill: colors.overhead }

  const countrySlices: BreakdownSlice[] = aggregateSlices(
    countries.map((c, i) => ({
      key: c.code,
      name: `${codeToFlag(c.code)} ${c.country}`,
      plainName: c.country,
      glyphLarge: <span className="text-4xl leading-none">{codeToFlag(c.code)}</span>,
      value: c.sales,
      fill: countryPalette[i % countryPalette.length] as string,
    })),
    othersStyle
  )

  const platformLabels: Record<string, string> = {
    META: t('productStatistics.breakdowns.metaAds'),
    GOOGLE: t('productStatistics.breakdowns.googleAds'),
  }

  const platformColors: Record<string, string> = {
    META: colors.meta,
    GOOGLE: colors.google,
  }

  const platformSlices: BreakdownSlice[] = aggregateSlices(
    Object.entries(platformBreakdown)
      .filter(([, value]) => value > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([platform, value], i) => ({
        key: platform,
        name: platformLabels[platform] ?? platform,
        plainName: platformLabels[platform] ?? platform,
        glyphLarge: platformIcon(platform, 'w-9 h-9 text-foreground/90'),
        labelIcon: platformLabelIcon(platform),
        value,
        fill: platformColors[platform] ?? (countryPalette[i % countryPalette.length] as string),
      })),
    othersStyle
  )

  if (!countrySlices.length && !platformSlices.length) return null

  const hasBoth = countrySlices.length > 0 && platformSlices.length > 0

  return (
    <div
      className={cn(
        // No vertical container padding and no items-start: columns carry
        // their own py and stretch to equal height, so the divider between
        // them runs edge to edge.
        'grid grid-cols-1 md:grid-cols-2',
        'rounded-xl border border-border/40 bg-card/30 px-6',
        className
      )}
    >
      {countrySlices.length > 0 && (
        <BreakdownCard
          id="product-revenue-by-country"
          title={t('productStatistics.breakdowns.byCountry')}
          centerCaption={t('productStatistics.kpi.revenue')}
          singleCaption={t('productStatistics.breakdowns.singleCountry')}
          slices={countrySlices}
          className={cn(hasBoth && 'md:pr-6')}
        />
      )}
      {platformSlices.length > 0 && (
        <BreakdownCard
          id="product-ad-spend-by-platform"
          title={t('productStatistics.breakdowns.byPlatform')}
          centerCaption={t('productStatistics.kpi.adSpend')}
          singleCaption={t('productStatistics.breakdowns.singlePlatform')}
          slices={platformSlices}
          className={cn(hasBoth && 'md:border-l md:border-border/40 md:pl-6')}
        />
      )}
    </div>
  )
}

interface BreakdownCardProps {
  id: string
  title: string
  /** Small muted caption under the center total (e.g. Revenue / Ad Spend). */
  centerCaption: string
  /** Caption for the single-source state (e.g. "Single ad platform"). */
  singleCaption: string
  slices: BreakdownSlice[]
  className?: string
}

function BreakdownCard({ id, title, centerCaption, singleCaption, slices, className }: BreakdownCardProps): ReactElement {
  const compactMoney = useCompactMoney()
  const total = slices.reduce((sum, s) => sum + s.value, 0)

  const chartConfig = useMemo(() => {
    const config: ChartConfig = {}
    for (const s of slices) config[s.key] = { label: s.name, color: s.fill }
    return config
  }, [slices])

  return (
    <div className={cn('min-w-0 py-8', className)}>
      <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground text-center mb-2">
        {title}
      </h3>
      <div>
        {slices.length === 1 ? (
          // Single source: there is nothing to break down, so render a
          // deliberate centered statement at donut height — keeps the two
          // columns symmetric without a meaningless one-slice ring.
          <div data-testid="breakdown-single" className="h-[368px] flex flex-col items-center justify-center gap-3">
            {slices[0]!.glyphLarge}
            <div className="flex items-baseline gap-2.5">
              <span className="text-lg font-semibold text-foreground">{slices[0]!.plainName}</span>
              <span className="font-mono text-lg font-bold text-foreground">
                {compactMoney(Math.round(slices[0]!.value))}
              </span>
            </div>
            <span className="text-sm text-muted-foreground">{singleCaption}</span>
          </div>
        ) : (
          <div data-testid="breakdown-donut">
            <ChartContainer id={id} config={chartConfig} className="mx-auto w-full h-[368px]">
              <PieChart margin={{ top: 16, left: 24, right: 24, bottom: 16 }}>
                <DonutSliceGradients idPrefix={`${id}-slice`} colors={slices.map((s) => s.fill)} />
                <Pie
                  data={slices}
                  cx="50%"
                  cy="50%"
                  innerRadius="55%"
                  outerRadius="70%"
                  paddingAngle={4}
                  cornerRadius={8}
                  dataKey="value"
                  stroke="none"
                  isAnimationActive={false}
                  labelLine={{
                    stroke: 'var(--muted-foreground)',
                    strokeWidth: 1,
                    strokeDasharray: '3 3',
                  }}
                  label={({ cx, cy, midAngle = 0, outerRadius, payload }) => {
                    const RADIAN = Math.PI / 180
                    const radius = Number(outerRadius) + 18
                    const x = cx + radius * Math.cos(-midAngle * RADIAN)
                    const y = cy + radius * Math.sin(-midAngle * RADIAN)
                    const isRightSide = Math.cos(-midAngle * RADIAN) > 0
                    const anchor = isRightSide ? 'start' : 'end'
                    const slice = payload as BreakdownSlice
                    const pct = total > 0 ? ((slice.value / total) * 100).toFixed(1) : '0'
                    // Brand icons render as SVG at the leader-line end,
                    // vertically centered between the two text lines; the
                    // text shifts outward to make room.
                    const textOffset = slice.labelIcon ? 27 : 0
                    const tx = isRightSide ? x + textOffset : x - textOffset
                    return (
                      <g>
                        {slice.labelIcon && (
                          <g transform={`translate(${isRightSide ? x : x - 21}, ${y - 10})`}>
                            {slice.labelIcon}
                          </g>
                        )}
                        <text
                          x={tx}
                          y={y - 9}
                          textAnchor={anchor}
                          dominantBaseline="middle"
                          className="font-bold fill-foreground"
                          style={{ fontSize: 16 }}
                        >
                          {compactMoney(Math.round(slice.value))}
                        </text>
                        <text
                          x={tx}
                          y={y + 11}
                          textAnchor={anchor}
                          dominantBaseline="middle"
                          className="fill-muted-foreground"
                          style={{ fontSize: 13 }}
                        >
                          {slice.name} {pct}%
                        </text>
                      </g>
                    )
                  }}
                >
                  {slices.map((s, i) => (
                    <Cell key={s.key} fill={donutSliceGradientFill(`${id}-slice`, i)} />
                  ))}
                  <Label
                    content={({ viewBox }) => {
                      if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                        return (
                          <text
                            x={viewBox.cx}
                            y={viewBox.cy}
                            textAnchor="middle"
                            dominantBaseline="middle"
                          >
                            <tspan
                              x={viewBox.cx}
                              y={(viewBox.cy || 0) - 8}
                              className="font-bold text-2xl"
                              fill="var(--foreground)"
                            >
                              {compactMoney(Math.round(total))}
                            </tspan>
                            <tspan
                              x={viewBox.cx}
                              y={(viewBox.cy || 0) + 16}
                              className="text-sm"
                              fill="var(--muted-foreground)"
                            >
                              {centerCaption}
                            </tspan>
                          </text>
                        )
                      }
                    }}
                  />
                </Pie>
                {/* Hover detail: every slice restates its stats (the adapter
                    renders its chrome regardless, so an empty tooltip would
                    show as a bare sliver); the synthetic "Others" slice
                    additionally lists what it merged. */}
                <ChartTooltip
                  cursor={false}
                  content={
                    <RechartsHoverAdapter
                      dataChartId={`chart-${id}`}
                      width={260}
                      estimatedHeight={200}
                      animatePosition={false}
                    >
                      {({ payload }) => {
                        const item =
                          Array.isArray(payload) && payload.length
                            ? (payload[0] as { payload?: BreakdownSlice }).payload
                            : undefined
                        if (!item) return null
                        const constituents = item.constituents
                        return (
                          <div className="p-4 space-y-2.5 min-w-[220px]">
                            <div className="flex items-baseline justify-between gap-4 text-sm">
                              <span className="min-w-0 truncate font-medium text-foreground">{item.name}</span>
                              <span className="font-mono whitespace-nowrap text-foreground">
                                {compactMoney(Math.round(item.value))}
                                <span className="text-xs text-muted-foreground/70 ml-2">
                                  {total > 0 ? ((item.value / total) * 100).toFixed(1) : '0'}%
                                </span>
                              </span>
                            </div>
                            {constituents && constituents.length > 0 && (
                              <div className="space-y-2 border-t border-border/30 pt-2.5">
                                {constituents.map((c) => (
                                  <div key={c.key} className="flex items-baseline justify-between gap-4 text-sm">
                                    <span className="min-w-0 truncate text-muted-foreground">{c.name}</span>
                                    <span className="font-mono whitespace-nowrap text-foreground/90">
                                      {compactMoney(Math.round(c.value))}
                                      <span className="text-xs text-muted-foreground/70 ml-2">
                                        {total > 0 ? ((c.value / total) * 100).toFixed(1) : '0'}%
                                      </span>
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )
                      }}
                    </RechartsHoverAdapter>
                  }
                  wrapperStyle={{ display: 'none' }}
                />
              </PieChart>
            </ChartContainer>
          </div>
        )}
      </div>
    </div>
  )
}
